import React, { useCallback, useEffect, useState } from 'react';
import { Lock, Unlock, Mail, KeyRound, RotateCcw, AlertTriangle, RefreshCw } from 'lucide-react';
import { displayOrderNumber } from '@platform/shared-types';
import { api } from '../../services/apiService';
import { hardwareApi, type RegisterWelcomeView } from '../../services/hardwareApi';
import { toastService } from '../../services/toastService';
import { pushNotify } from '../../services/websocketService';
import { lockReceiptText } from '../../pages/inventory/LockReceiptList';
import { RubyLoader } from '../RubyLoader';
import { RubyGemMark } from '../RubyGemMark';
import { RubyModal } from '../finance/financeUi';
import { sendUnlockCodeEmails } from '../../pages/inventory/welcomeUnlockEmail';

/**
 * WELCOME SETUP — the "Hello from Ruby" switch for one terminal (Control tab).
 *
 * Fresh shows the welcome on the next power-on; the till flips it to Set up by
 * itself when the store finishes. The shipping lock is a separate fact: Reset
 * to Fresh never re-locks a device — only Lock device does.
 *
 * Everything here is read from and written to hardware-service
 * (`/lifecycle/registers/{id}/welcome`, `/lifecycle/orders/{id}/welcome/*`);
 * every action leaves a row on the unit's own history.
 */

interface Props {
  /** The register row id — the same id the terminals list calls `terminalId`. */
  registerId: string;
  tenantId: string;
  /** For the live terminal:lock / terminal:unlock push to the running till. */
  locationId?: string;
  locationName?: string;
}

const MAX_TRIES = 5;

/** Steps shown on every till; the model-specific ones appear once the till reports them. */
const CORE_STEPS = ['wifi', 'time'] as const;
const STEP_LABEL: Record<string, string> = {
  wifi: 'Wi-Fi', time: 'Time', printer: 'Printer', display: 'Customer screen',
  scanner: 'Scanner', reader: 'Card reader', pair: 'Paired till',
};
const STEP_ORDER = ['wifi', 'time', 'printer', 'display', 'scanner', 'reader', 'pair'];

const when = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const Row: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-2.5 border-b border-stone-100 last:border-b-0 text-sm">
    <span className="text-stone-500">{k}</span>
    <span className="text-stone-900 font-semibold text-right">{v}</span>
  </div>
);

const BTN = 'inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-xl text-sm font-semibold whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
const BTN_LINE = `${BTN} bg-white text-stone-900 border border-stone-200 hover:bg-stone-50`;
const BTN_DARK = `${BTN} bg-stone-950 text-white hover:bg-black`;

type Busy = null | 'load' | 'fresh' | 'setup' | 'resend' | 'new_code' | 'lock' | 'unlock';

/** While the device has not fetched its Fresh/locked state yet, re-read this often. */
const SEEN_POLL_MS = 15000;

const WelcomeSetupCard: React.FC<Props> = ({ registerId, tenantId, locationId, locationName }) => {
  const [view, setView] = useState<RegisterWelcomeView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>('load');
  const [confirm, setConfirm] = useState<null | 'new_code' | 'lock'>(null);

  const load = useCallback(async () => {
    try {
      setView(await hardwareApi.getRegisterWelcome(registerId, tenantId));
      setLoadError(null);
    } catch (e: any) {
      setLoadError(e?.message || 'Could not read the welcome setup');
    } finally {
      setBusy((b) => (b === 'load' ? null : b));
    }
  }, [registerId, tenantId]);

  useEffect(() => { void load(); }, [load]);

  const welcome = view?.welcome || null;
  const mode = welcome?.mode || 'setup';
  const locked = !!welcome?.locked;
  const lock = view?.lock || null;
  const orderId = view?.orderId || welcome?.orderId;
  /** Fresh or locked: the device must fetch that state before it ships (or before the lock bites). */
  const awaitsDevice = mode === 'fresh' || locked;
  const waitingForDevice = awaitsDevice && !welcome?.deviceSeenAt;

  useEffect(() => {
    if (!waitingForDevice) return undefined;
    const t = setInterval(() => { void load(); }, SEEN_POLL_MS);
    return () => clearInterval(t);
  }, [waitingForDevice, load]);

  const act = async (b: Exclude<Busy, null | 'load'>, fn: () => Promise<unknown>, ok: string) => {
    setBusy(b);
    try {
      await fn();
      toastService.success(ok);
      await load();
    } catch (e: any) {
      toastService.error(e?.message || 'That did not go through');
    } finally {
      setBusy(null);
    }
  };

  const setAction = (action: 'fresh' | 'setup' | 'lock' | 'unlock') => hardwareApi.setRegisterWelcome(registerId, tenantId, action);

  /**
   * Lock / Unlock device: the welcome lock (refuses every sign-in and refresh),
   * then the same live push the Control tab's Lock uses, so a till that is
   * running right now shows its lock screen at once (and leaves it on unlock).
   * The push is best effort — the till's own 60 s check catches it otherwise.
   */
  const lockAction = async (action: 'lock' | 'unlock') => {
    await setAction(action);
    await pushNotify(action === 'lock' ? 'terminal:lock' : 'terminal:unlock', {
      tenantId, ...(locationId ? { locationId } : {}), terminalId: registerId,
    }, { source: 'super_admin' });
  };

  /** Who the email is from and about — read fresh so a renamed business is right. */
  const emailContext = async (oid: string) => {
    const [meta, ord] = await Promise.all([
      api.tenants.getMetadata(tenantId, { silent: true }).catch(() => null),
      hardwareApi.getOrder(oid).catch(() => null),
    ]);
    return {
      businessName: (meta as any)?.businessName || meta?.name || '',
      ownerName: meta?.ownerName || '',
      ownerEmail: meta?.ownerEmail || meta?.email || '',
      orderNumber: ord?.order?.orderNo ? displayOrderNumber(ord.order.orderNo) : '',
    };
  };

  /** Resend = the SAME code (issue is idempotent), emailed again, stamped. */
  const resend = () => act('resend', async () => {
    if (!orderId) throw new Error('This terminal is not on an order');
    const issued = await hardwareApi.issueOrderWelcome(orderId, { shipUnlocked: lock?.shipUnlocked });
    const ctx = await emailContext(orderId);
    const to = issued.ownerEmail || ctx.ownerEmail;
    if (!to) throw new Error('No owner email on file for this business');
    const { stamped } = await sendUnlockCodeEmails({
      orderId, to, code: issued.code,
      ownerName: ctx.ownerName, businessName: ctx.businessName, locationName,
      orderNumber: ctx.orderNumber,
      devices: issued.devices.map((d) => ({ label: d.label, registerName: d.registerName })),
    });
    if (!stamped) toastService.warning('Emailed, but the order did not record it — "Order code" may still read not emailed.');
  }, 'Unlock code emailed again');

  /** New code: the old one stops working, the new one goes out the same way. */
  const newCode = () => act('new_code', async () => {
    if (!orderId) throw new Error('This terminal is not on an order');
    await hardwareApi.newOrderWelcomeCode(orderId);
    /* Same recipient resolution as Resend: issue is idempotent, so it hands back
       the NEW code plus the order's owner email and devices. */
    const [issued, ctx] = await Promise.all([
      hardwareApi.issueOrderWelcome(orderId, { shipUnlocked: lock?.shipUnlocked }),
      emailContext(orderId),
    ]);
    const to = issued.ownerEmail || ctx.ownerEmail;
    if (!to) throw new Error('New code issued, but there is no owner email to send it to');
    const { stamped } = await sendUnlockCodeEmails({
      orderId, to, code: issued.code,
      ownerName: ctx.ownerName, businessName: ctx.businessName, locationName,
      orderNumber: ctx.orderNumber,
      devices: issued.devices.map((d) => ({ label: d.label, registerName: d.registerName })),
    });
    if (!stamped) toastService.warning('Emailed, but the order did not record it — "Order code" may still read not emailed.');
  }, 'New unlock code issued and emailed');

  const stepRows = (() => {
    const progress = welcome?.progress || {};
    const ids = STEP_ORDER.filter((s) => (CORE_STEPS as readonly string[]).includes(s) || progress[s]);
    return ids.map((s) => {
      const p = progress[s];
      return { id: s, label: STEP_LABEL[s] || s, value: p ? (p.status === 'later' ? 'Later' : `Done · ${when(p.at)}`) : '—' };
    });
  })();

  const disabled = busy !== null;

  return (
    <div className="bg-white text-stone-800 rounded-2xl shadow-sm border border-stone-200/60 overflow-hidden">
      <div className="relative flex items-center gap-3 px-5 py-4">
        <div className="w-9 h-9 rounded-[10px] bg-[#FFF1F3] flex items-center justify-center shrink-0">
          <RubyGemMark size={20} />
        </div>
        <h3 className="text-stone-900 font-bold text-[15px]">Welcome setup</h3>
        <span aria-hidden className="absolute inset-x-0 bottom-0 h-[2px]" style={{ background: 'linear-gradient(90deg,#DC143C,#9F1239 60%,transparent)' }} />
      </div>

      {busy === 'load' ? (
        <RubyLoader variant="section" className="py-8" />
      ) : loadError ? (
        <div className="p-5 flex flex-wrap items-center gap-3 text-sm text-stone-600">
          <AlertTriangle size={16} className="text-amber-600 shrink-0" />
          <span className="flex-1 min-w-[200px]">Welcome setup could not load — {loadError}</span>
          <button className={BTN_LINE} onClick={() => { setBusy('load'); void load(); }}>
            <RefreshCw size={14} /> Try again
          </button>
        </div>
      ) : (
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-stone-400 mb-2.5">Mode</p>
            <div className="inline-flex bg-stone-100 rounded-xl p-1" role="radiogroup" aria-label="Welcome mode">
              {(['fresh', 'setup'] as const).map((m) => (
                <button
                  key={m}
                  role="radio"
                  aria-checked={mode === m}
                  disabled={disabled}
                  onClick={() => mode !== m && act(m, () => setAction(m), m === 'fresh' ? 'Welcome shows on the next start' : 'Set up — the welcome will not show')}
                  className={`min-h-[44px] px-5 rounded-[9px] text-sm font-semibold transition-colors disabled:cursor-not-allowed ${
                    mode === m ? 'bg-white text-stone-950 shadow-sm' : 'text-stone-500 hover:text-stone-800'}`}
                >
                  {busy === m ? <RubyGemMark size={14} drawing color="currentColor" /> : m === 'fresh' ? 'Fresh' : 'Set up'}
                </button>
              ))}
            </div>
            <div className="mt-4">
              <Row k="Shipping lock" v={locked ? (welcome?.lockedBy === 'super_admin' ? 'Locked by RubyTill' : 'Locked') : 'Unlocked'} />
              {awaitsDevice && (
                <Row
                  k="On the device"
                  v={<span className={welcome?.deviceSeenAt ? 'text-emerald-700' : 'text-stone-500 font-normal'}>{lockReceiptText(welcome?.deviceSeenAt)}</span>}
                />
              )}
              <Row
                k="Order code"
                v={!lock ? 'None issued' : lock.emailedAt
                  ? `Emailed ${when(lock.emailedAt)}${lock.emailedToMasked ? ` · ${lock.emailedToMasked}` : ''}`
                  : 'Issued, not emailed'}
              />
              <Row
                k="Wrong tries"
                v={lock ? (
                  <span className={lock.held ? 'text-amber-700' : ''}>
                    {lock.wrongTries} of {MAX_TRIES}{lock.held ? ' · held' : ''}
                  </span>
                ) : '—'}
              />
            </div>
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-stone-400 mb-2.5">On this device</p>
            <Row k="Welcome opened" v={when(welcome?.openedAt)} />
            <Row k="Unlocked" v={when(welcome?.unlockedAt)} />
            {stepRows.map((s) => <Row key={s.id} k={s.label} v={s.value} />)}
            <Row k="Setup finished" v={when(welcome?.completedAt)} />

            <div className="flex flex-wrap gap-2 mt-4">
              <button className={BTN_LINE} disabled={disabled || !orderId || !lock} onClick={resend}>
                {busy === 'resend' ? <RubyGemMark size={14} drawing color="currentColor" /> : <Mail size={14} />} Resend code
              </button>
              <button className={BTN_LINE} disabled={disabled || !orderId || !lock} onClick={() => setConfirm('new_code')}>
                {busy === 'new_code' ? <RubyGemMark size={14} drawing color="currentColor" /> : <KeyRound size={14} />} New code
              </button>
              <button className={BTN_DARK} disabled={disabled || mode === 'fresh'} onClick={() => act('fresh', () => setAction('fresh'), 'Reset to Fresh — the welcome shows on the next start')}>
                {busy === 'fresh' ? <RubyGemMark size={14} drawing color="currentColor" /> : <RotateCcw size={14} />} Reset to Fresh
              </button>
              {locked ? (
                <button className={BTN_LINE} disabled={disabled} onClick={() => act('unlock', () => lockAction('unlock'), 'Device unlocked')}>
                  {busy === 'unlock' ? <RubyGemMark size={14} drawing color="currentColor" /> : <Unlock size={14} />} Unlock device
                </button>
              ) : (
                <button className={BTN_LINE} disabled={disabled} onClick={() => setConfirm('lock')}>
                  {busy === 'lock' ? <RubyGemMark size={14} drawing color="currentColor" /> : <Lock size={14} />} Lock device
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <RubyModal
        open={confirm === 'new_code'}
        header="white"
        icon={<KeyRound size={18} className="text-[#DC143C]" />}
        title="Issue a new unlock code?"
        confirmLabel="Issue and email"
        busy={busy === 'new_code'}
        onClose={() => setConfirm(null)}
        onConfirm={() => { void newCode().finally(() => setConfirm(null)); }}
      >
        <p>The current code stops working on every device in this order.</p>
        <p className="text-stone-500">The new code is emailed to the owner. Wrong tries reset to 0.</p>
      </RubyModal>

      <RubyModal
        open={confirm === 'lock'}
        header="white"
        icon={<Lock size={18} className="text-[#DC143C]" />}
        title="Lock this device?"
        confirmLabel="Lock device"
        busy={busy === 'lock'}
        onClose={() => setConfirm(null)}
        onConfirm={() => { void act('lock', () => lockAction('lock'), 'Device locked').finally(() => setConfirm(null)); }}
      >
        <p>The till locks at once and nobody can sign in until you unlock it.</p>
        <p className="text-stone-500">Only Unlock device here opens it again.</p>
      </RubyModal>
    </div>
  );
};

export default WelcomeSetupCard;
